import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Plus,
  Search,
  FileText,
  Clock,
  MapPin,
  DollarSign,
  Package,
  Eye,
  MessageSquare,
  Send,
  ChevronRight,
  Filter,
  Calendar,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

const emptyRFQ = {
  title: '',
  category: '',
  description: '',
  quantity: '',
  unit: 'pcs',
  target_price: '',
  currency: 'USD',
  delivery_location: '',
  delivery_deadline: '',
  requirements: [],
  visibility: 'public'
};

// Status badge colors
const statusColors = {
  open: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 dark:bg-gray-800 text-gray-700',
  awarded: 'bg-blue-100 text-blue-700',
  expired: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700'
};

// RFQ Card Component
function RFQCard({ rfq, isBuyer = false, onView }) {
  const daysLeft = rfq.expires_at 
    ? Math.ceil((new Date(rfq.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;
  
  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(rfq)}
      data-testid={`rfq-card-${rfq._id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{rfq.title}</h3>
            <p className="text-sm text-muted-foreground truncate">
              {rfq.buyer_name}
            </p>
          </div>
          <Badge className={statusColors[rfq.status] || statusColors.open}>
            {rfq.status}
          </Badge>
        </div>
        
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {rfq.description}
        </p>
        
        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-3">
          <div className="flex items-center gap-1">
            <Package className="h-3 w-3" />
            {rfq.category?.replace(/_/g, ' ') || 'General'}
          </div>
          {rfq.quantity && (
            <div className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {rfq.quantity} {rfq.unit || 'units'}
            </div>
          )}
          {rfq.delivery_location && (
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {rfq.delivery_location}
            </div>
          )}
          {rfq.target_price && (
            <div className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              Target: {rfq.currency} {rfq.target_price}
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {rfq.views_count || 0} views
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {rfq.quotes_count || 0} quotes
            </span>
          </div>
          {daysLeft > 0 ? (
            <span className="flex items-center gap-1 text-amber-600">
              <Clock className="h-3 w-3" />
              {daysLeft}d left
            </span>
          ) : (
            <span className="text-red-500">{t("rfq.expired") || "Expired"}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Create RFQ Dialog
function CreateRFQDialog({
  open, onOpenChange, onSuccess }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyRFQ);
  const [requirement, setRequirement] = useState('');
  const [errors, setErrors] = useState({});
  
  const { data: categories } = useQuery({
    queryKey: ['rfq-categories'],
    queryFn: () => api.get('/rfq/categories')
  });
  
  const createRFQ = useMutation({
    mutationFn: (data) => api.post('/rfq', data),
    onSuccess: (data) => {
      toast.success('RFQ created successfully');
      onSuccess?.(data);
      onOpenChange(false);
      setFormData(emptyRFQ);
      setErrors({});
      setRequirement('');
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to create RFQ');
    }
  });
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    
    createRFQ.mutate({
      ...formData,
      quantity: formData.quantity ? parseInt(formData.quantity) : null,
      target_price: formData.target_price ? parseFloat(formData.target_price) : null
    });
  };
  
  const addRequirement = () => {
    if (requirement.trim()) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, requirement.trim()]
      }));
      setRequirement('');
    }
  };
  
  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };
  
  return (
    <Dialog open={open} onOpenChange={(open) => {
        onOpenChange(open);
        if(!open) {
          setFormData(emptyRFQ);
          setErrors({});
          setRequirement('');
        }
    }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-2 flex-shrink-0">
          <DialogTitle>{t("rfq.createRfq") || "Create Request for Quote"}</DialogTitle>
          <CardDescription>
            Describe what you need and receive quotes from suppliers
          </CardDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label className={errors.title ? "text-destructive" : ""}>
                  Title <span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  value={formData.title}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, title: e.target.value }));
                    if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                  }}
                  className={errors.title ? "border-destructive" : ""}
                  maxLength={100}
                  placeholder="e.g., 1000 Custom T-Shirts with Logo Print"
                />
                {errors.title && <p className="text-xs text-destructive mt-1">{errors.title}</p>}
                {!errors.title && (
                  <div className="flex justify-end text-xs text-muted-foreground mt-1">
                    <span>{formData.title.length}/100</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className={errors.category ? "text-destructive" : ""}>
                  Category <span className="text-destructive ml-1">*</span>
                </Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => {
                    setFormData(prev => ({ ...prev, category: v }));
                    if (errors.category) setErrors(prev => ({ ...prev, category: null }));
                  }}
                >
                  <SelectTrigger className={errors.category ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.categories?.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-xs text-destructive mt-1">{errors.category}</p>}
              </div>
              
              <div className="space-y-2">
                <Label>{t("rfq.visibility") || "Visibility"}</Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, visibility: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">{t("rfq.public") || "Public - All suppliers can see"}</SelectItem>
                    <SelectItem value="private">{t("rfq.private") || "Private - Invite only"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className={errors.description ? "text-destructive" : ""}>
                Description <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, description: e.target.value }));
                  if (errors.description) setErrors(prev => ({ ...prev, description: null }));
                }}
                className={errors.description ? "border-destructive" : ""}
                maxLength={1000}
                placeholder="Describe your requirements in detail..."
                rows={4}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                {errors.description ? <span className="text-destructive">{errors.description}</span> : <span></span>}
                <span>{formData.description.length}/1000</span>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t("rfq.quantity") || "Quantity"}</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="e.g., 1000"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t("rfq.unit") || "Unit"}</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcs">{t("rfq.pieces") || "Pieces"}</SelectItem>
                    <SelectItem value="kg">{t("rfq.kilograms") || "Kilograms"}</SelectItem>
                    <SelectItem value="m">{t("rfq.meters") || "Meters"}</SelectItem>
                    <SelectItem value="sqm">{t("rfq.squareMeters") || "Square Meters"}</SelectItem>
                    <SelectItem value="units">{t("rfq.units") || "Units"}</SelectItem>
                    <SelectItem value="sets">{t("rfq.sets") || "Sets"}</SelectItem>
                    <SelectItem value="boxes">{t("rfq.boxes") || "Boxes"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Target Price ({formData.currency})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.target_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_price: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("rfq.deliveryLocation") || "Delivery Location"}</Label>
                <Input
                  value={formData.delivery_location}
                  onChange={(e) => setFormData(prev => ({ ...prev, delivery_location: e.target.value }))}
                  placeholder="e.g., Los Angeles, CA"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t("rfq.deliveryDeadline") || "Delivery Deadline"}</Label>
                <Input
                  type="date"
                  value={formData.delivery_deadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, delivery_deadline: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("rfq.specialRequirements") || "Special Requirements"}</Label>
              <div className="flex gap-2">
                <Input
                  value={requirement}
                  onChange={(e) => setRequirement(e.target.value)}
                  placeholder="Add a requirement"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addRequirement();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={addRequirement}>
                  Add
                </Button>
              </div>
              {formData.requirements.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.requirements.map((req, i) => (
                    <Badge key={i} variant="secondary" className="cursor-pointer hover:bg-destructive hover:text-white transition-colors" onClick={() => removeRequirement(i)}>
                      {req} &times;
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
          <DialogFooter className="pt-2 gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={createRFQ.isPending}
              data-testid="submit-rfq-btn"
              className="relative z-50"
            >
              {createRFQ.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Post RFQ
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Main RFQ Page
export default function RFQPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('browse');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  
  // Get categories
  const { data: categoriesData } = useQuery({
    queryKey: ['rfq-categories'],
    queryFn: () => api.get('/rfq/categories')
  });
  
  // Browse public RFQs
  const { data: publicRFQs, isLoading: loadingPublic } = useQuery({
    queryKey: ['public-rfqs', searchQuery, categoryFilter],
    queryFn: () => api.get('/rfq', {
      params: {
        search: searchQuery || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined
      }
    })
  });
  
  // My RFQs (as buyer)
  const { data: myRFQs, isLoading: loadingMy } = useQuery({
    queryKey: ['my-rfqs'],
    queryFn: () => api.get('/rfq/my')
  });
  
  // RFQ Stats
  const { data: stats } = useQuery({
    queryKey: ['rfq-stats'],
    queryFn: () => api.get('/rfq/stats/my')
  });
  
  const handleRFQSuccess = () => {
    queryClient.invalidateQueries(['my-rfqs']);
    queryClient.invalidateQueries(['rfq-stats']);
    setActiveTab('my-rfqs');
  };
  
  const handleViewRFQ = (rfq) => {
    setSelectedRFQ(rfq);
  };
  
  const categories = categoriesData?.categories || [];
  
  return (
    <div className="space-y-4 animate-fade-in" data-testid="rfq-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-display font-bold">{t("rfq.title") || "Request for Quotes"}</h1>
          <p className="text-sm text-muted-foreground">{t("rfq.subtitle") || "Browse sourcing requests or post your own"}</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-rfq-btn">
          <Plus className="h-4 w-4 mr-2" />
          Post RFQ
        </Button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold">{stats.buyer?.rfqs_created || 0}</p>
              <p className="text-xs text-muted-foreground">{t("rfq.rfqsPosted") || "RFQs Posted"}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold text-green-600">{stats.buyer?.rfqs_open || 0}</p>
              <p className="text-xs text-muted-foreground">{t("rfq.openRfqs") || "Open RFQs"}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold">{stats.supplier?.quotes_submitted || 0}</p>
              <p className="text-xs text-muted-foreground">{t("rfq.quotesSent") || "Quotes Sent"}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold text-amber-600">{stats.supplier?.win_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">{t("rfq.winRate") || "Win Rate"}</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="browse">
            <Search className="h-4 w-4 mr-2" />
            Browse RFQs
          </TabsTrigger>
          <TabsTrigger value="my-rfqs">
            <FileText className="h-4 w-4 mr-2" />
            My RFQs ({myRFQs?.total || 0})
          </TabsTrigger>
        </TabsList>
        
        {/* Browse Tab */}
        <TabsContent value="browse" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search RFQs..."
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("rfq.allCategories") || "All Categories"}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* RFQ Grid */}
          {loadingPublic ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : publicRFQs?.rfqs?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">{t("rfq.noRfqsFound") || "No RFQs found"}</h3>
                <p className="text-muted-foreground mb-4">
                  Be the first to post a sourcing request
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Post RFQ
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publicRFQs?.rfqs?.map((rfq) => (
                <RFQCard key={rfq._id} rfq={rfq} onView={handleViewRFQ} />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* My RFQs Tab */}
        <TabsContent value="my-rfqs" className="space-y-4">
          {loadingMy ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : myRFQs?.rfqs?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">{t("rfq.noRfqsYet") || "No RFQs yet"}</h3>
                <p className="text-muted-foreground mb-4">
                  Post your first sourcing request to get quotes from suppliers
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Post RFQ
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myRFQs?.rfqs?.map((rfq) => (
                <RFQCard key={rfq._id} rfq={rfq} isBuyer={true} onView={handleViewRFQ} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Create RFQ Dialog */}
      <CreateRFQDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleRFQSuccess}
      />
    </div>
  );
}
